/**
 * Notification menu items (bell). Recent unconfirmed jobs stay on
 * Calendar; stale provisional work (>14 days) also appears here.
 * Badge uses id + stateToken: seen when opened; re-triggers when
 * stateToken changes (newly urgent).
 */

export type NotificationKind =
  | 'stale_provisional'
  | 'week_release'
  | 'overdue_invoices'
  | 'renewal'
  | 'sync'
  | 'scheduled_job';

export type NotificationTarget =
  | { type: 'calendar_unconfirmed' }
  | { type: 'week_signoff'; weekStart: string }
  | { type: 'money_overdue' }
  | { type: 'renewal'; id: string }
  | { type: 'settings_sync' }
  | { type: 'scheduled_job'; jobName: string };

export type NotificationItem = {
  /** Stable identity across renders. */
  id: string;
  kind: NotificationKind;
  /**
   * Urgency fingerprint. Badge clears when seen[id] === stateToken.
   * Changing stateToken (e.g. invoice newly overdue, renewal enters
   * lead / expires) re-triggers even if the same id was seen before.
   */
  stateToken: string;
  /** Lower = more urgent. */
  priority: number;
  title: string;
  subtitle: string;
  target: NotificationTarget;
};

export const NOTIF_PRIORITY = {
  stale_provisional: 8,
  week_release: 9,
  overdue_invoices: 10,
  renewal: 20,
  sync: 30,
  scheduled_job: 40,
} as const;

export type SeenMap = Record<string, string>;

/** Count items whose current stateToken differs from last-seen. */
export function unseenNotificationCount(
  items: NotificationItem[],
  seen: SeenMap,
): number {
  return items.filter((i) => seen[i.id] !== i.stateToken).length;
}

/** Opening the menu marks every current item's state as seen. */
export function markNotificationsSeen(
  items: NotificationItem[],
  prev: SeenMap = {},
): SeenMap {
  const next: SeenMap = { ...prev };
  for (const i of items) {
    next[i.id] = i.stateToken;
  }
  return next;
}

function parseISODate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!, 12, 0, 0));
}

function formatISODate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function renewalStateToken(
  expiryDate: string,
  todayISO: string,
): string {
  return expiryDate < todayISO ? 'expired' : 'in_lead';
}

export function isRenewalInLead(opts: {
  expiryDate: string | null;
  leadTimeDays: number;
  todayISO: string;
  active: boolean;
  deleted: boolean;
}): boolean {
  if (opts.deleted || !opts.active || !opts.expiryDate) return false;
  const exp = parseISODate(opts.expiryDate);
  exp.setUTCDate(exp.getUTCDate() - opts.leadTimeDays);
  return formatISODate(exp) <= opts.todayISO;
}

export function daysRemaining(expiryISO: string, todayISO: string): number {
  const a = parseISODate(expiryISO).getTime();
  const b = parseISODate(todayISO).getTime();
  return Math.round((a - b) / 86_400_000);
}

export type OverdueInvoiceRow = {
  id: string;
  invoice_number: string | null;
  client_name: string;
  due_date: string | null;
  gross_pence: number | null;
};

/** One grouped row: tap → Money with Overdue filter. */
export function notifOverdueInvoices(
  rows: OverdueInvoiceRow[],
): NotificationItem[] {
  if (rows.length === 0) return [];
  const ids = [...rows.map((r) => r.id)].sort();
  const total = rows.reduce((s, r) => s + (r.gross_pence ?? 0), 0);
  const pounds = `£${(total / 100).toFixed(2)}`;
  return [
    {
      id: 'overdue_invoices',
      kind: 'overdue_invoices',
      stateToken: ids.join(','),
      priority: NOTIF_PRIORITY.overdue_invoices,
      title:
        rows.length === 1
          ? '1 overdue invoice'
          : `${rows.length} overdue invoices`,
      subtitle: `${pounds} outstanding`,
      target: { type: 'money_overdue' },
    },
  ];
}

export type RenewalRow = {
  id: string;
  title: string;
  issuer: string | null;
  expiry_date: string | null;
  lead_time_days: number;
  active: boolean;
  deleted_at: string | null;
};

export function notifRenewals(
  rows: RenewalRow[],
  todayISO: string,
): NotificationItem[] {
  return rows
    .filter((r) =>
      isRenewalInLead({
        expiryDate: r.expiry_date,
        leadTimeDays: r.lead_time_days,
        todayISO,
        active: r.active,
        deleted: !!r.deleted_at,
      }),
    )
    .sort((a, b) => (a.expiry_date ?? '').localeCompare(b.expiry_date ?? ''))
    .map((r) => {
      const rem =
        r.expiry_date != null ? daysRemaining(r.expiry_date, todayISO) : null;
      const remLabel =
        rem == null
          ? ''
          : rem < 0
            ? `${Math.abs(rem)} days overdue`
            : `${rem} days left`;
      const stateToken = renewalStateToken(r.expiry_date!, todayISO);
      return {
        id: `renewal:${r.id}`,
        kind: 'renewal' as const,
        stateToken,
        priority: NOTIF_PRIORITY.renewal,
        title: r.title,
        subtitle: `${r.issuer ? `${r.issuer} · ` : ''}${r.expiry_date ?? ''}${
          remLabel ? ` · ${remLabel}` : ''
        }`,
        target: { type: 'renewal' as const, id: r.id },
      };
    });
}

/** Client-only: failed syncs or stuck outbox. */
export function notifSync(pending: number, failed: number): NotificationItem[] {
  if (pending <= 0 && failed <= 0) return [];
  return [
    {
      id: 'sync',
      kind: 'sync',
      stateToken: `p${pending}:f${failed}`,
      priority: NOTIF_PRIORITY.sync,
      title: failed > 0 ? 'Sync failed' : 'Items waiting to sync',
      subtitle:
        failed > 0
          ? `${failed} failed · ${pending} pending`
          : `${pending} change${pending === 1 ? '' : 's'} in the outbox`,
      target: { type: 'settings_sync' },
    },
  ];
}

export type ScheduledJobAlert = {
  jobName: string;
  description: string;
  /** failure | overdue (no success) | not_running (cron did not fire) */
  reason: 'failure' | 'overdue' | 'not_running';
  /** Fingerprint — run id or lastSuccessAt / never */
  stateToken: string;
};

export function notifScheduledJobs(
  alerts: ScheduledJobAlert[],
): NotificationItem[] {
  return alerts.map((a) => ({
    id: `scheduled_job:${a.jobName}`,
    kind: 'scheduled_job' as const,
    stateToken: `${a.reason}:${a.stateToken}`,
    priority: NOTIF_PRIORITY.scheduled_job,
    title:
      a.jobName === 'receipt_push_unknown'
        ? 'Receipt push outcome unknown'
        : a.jobName === 'receipt_push_queued'
          ? 'Receipt push queued too long'
          : a.reason === 'failure'
          ? 'Scheduled job failed'
          : a.reason === 'not_running'
            ? 'Scheduled job did not run'
            : 'Scheduled job overdue',
    subtitle: a.description,
    target: { type: 'scheduled_job' as const, jobName: a.jobName },
  }));
}

export type StaleProvisionalRow = {
  id: string;
  work_date: string;
};

/** Grouped row: tap → Calendar unconfirmed list. */
export function notifStaleProvisional(
  rows: StaleProvisionalRow[],
): NotificationItem[] {
  if (rows.length === 0) return [];
  const ids = [...rows.map((r) => r.id)].sort();
  const oldest = [...rows]
    .map((r) => r.work_date)
    .sort()[0];
  return [
    {
      id: 'stale_provisional',
      kind: 'stale_provisional',
      stateToken: ids.join(','),
      priority: NOTIF_PRIORITY.stale_provisional,
      title:
        rows.length === 1
          ? '1 job still provisional after 14 days'
          : `${rows.length} jobs still provisional after 14 days`,
      subtitle: oldest
        ? `Oldest ${oldest} — will not be invoiced until confirmed`
        : 'Will not be invoiced until confirmed',
      target: { type: 'calendar_unconfirmed' },
    },
  ];
}

export function sortNotifications(
  items: NotificationItem[],
): NotificationItem[] {
  const kindOrder: Record<NotificationKind, number> = {
    stale_provisional: 0,
    week_release: 1,
    overdue_invoices: 2,
    renewal: 3,
    sync: 4,
    scheduled_job: 5,
  };
  return [...items].sort((a, b) => {
    if (a.priority !== b.priority) return a.priority - b.priority;
    if (kindOrder[a.kind] !== kindOrder[b.kind]) {
      return kindOrder[a.kind] - kindOrder[b.kind];
    }
    return a.id.localeCompare(b.id);
  });
}

export type WeekReleaseNudge = {
  weekStart: string;
  pendingCount: number;
  pendingDays: number;
  pendingPence: number;
  saturdayEmpty: boolean;
};

export function notifWeekRelease(nudge: WeekReleaseNudge | null): NotificationItem[] {
  if (!nudge || nudge.pendingCount <= 0) return [];
  const pounds = `£${Math.round(nudge.pendingPence / 100).toLocaleString('en-GB')}`;
  const days = `${nudge.pendingDays} day${nudge.pendingDays === 1 ? '' : 's'}`;
  return [
    {
      id: 'week_release',
      kind: 'week_release',
      stateToken: `${nudge.weekStart}:${nudge.pendingCount}:${nudge.pendingPence}:${nudge.saturdayEmpty ? 'empty' : 'pending'}`,
      priority: NOTIF_PRIORITY.week_release,
      title: nudge.saturdayEmpty
        ? 'Saturday found nothing released'
        : `${days} · ${pounds} not yet released`,
      subtitle: nudge.saturdayEmpty
        ? `${days} · ${pounds} still waiting — this was not an empty week`
        : 'Review the week and release before the Saturday run',
      target: { type: 'week_signoff', weekStart: nudge.weekStart },
    },
  ];
}

export function buildServerNotifications(opts: {
  todayISO: string;
  overdueInvoices: OverdueInvoiceRow[];
  renewals: RenewalRow[];
  scheduledJobs: ScheduledJobAlert[];
  staleProvisional?: StaleProvisionalRow[];
  weekRelease?: WeekReleaseNudge | null;
}): NotificationItem[] {
  return sortNotifications([
    ...notifStaleProvisional(opts.staleProvisional ?? []),
    ...notifWeekRelease(opts.weekRelease ?? null),
    ...notifOverdueInvoices(opts.overdueInvoices),
    ...notifRenewals(opts.renewals, opts.todayISO),
    ...notifScheduledJobs(opts.scheduledJobs),
  ]);
}
